import { Store } from '@/core/flux.service';
import type { AgentBuilderGraph, AgentBuilderVersion } from '@/agentBuilder/agentBuilder.types';
import type { InitializeMode, PersistenceLevel } from '@/core/flux.service';

interface AgentBuilderState {
  currentAgentBuilder?: AgentBuilderGraph;
  selectedNodeId?: string;
  isDirty: boolean;
  versions: AgentBuilderVersion[];
  selectedVersionId: string | null;
  draftBaseVersionId: string | null;
  versionsLoading: boolean;
}

type AgentBuilderDehydratedState = AgentBuilderState;

export class AgentBuilderStore extends Store {
  static readonly storeName = 'sqAgentBuilderStore';
  persistenceLevel: PersistenceLevel = 'NONE';

  initialize(initializeMode: InitializeMode) {
    const initialState: AgentBuilderState = {
      currentAgentBuilder: undefined,
      selectedNodeId: undefined,
      isDirty: false,
      versions: [],
      selectedVersionId: null,
      draftBaseVersionId: null,
      versionsLoading: false,
    };
    this.state = this.immutable(initialState);
  }

  get currentAgentBuilder(): AgentBuilderGraph | undefined {
    return this.state.get('currentAgentBuilder');
  }

  get selectedNodeId(): string | undefined {
    return this.state.get('selectedNodeId');
  }

  get isDirty(): boolean {
    return this.state.get('isDirty') || false;
  }

  get versions(): AgentBuilderVersion[] {
    return this.state.get('versions') || [];
  }

  get selectedVersionId(): string | null {
    return this.state.get('selectedVersionId') ?? null;
  }

  get draftBaseVersionId(): string | null {
    return this.state.get('draftBaseVersionId') ?? null;
  }

  get versionsLoading(): boolean {
    return this.state.get('versionsLoading') || false;
  }

  dehydrate(): AgentBuilderDehydratedState {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: Partial<AgentBuilderDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  protected readonly handlers = {
    AGENT_BUILDER_SET_CURRENT: (payload: { agentBuilder?: AgentBuilderGraph }) => {
      this.state.set('currentAgentBuilder', payload.agentBuilder);
      const hasDraft = payload.agentBuilder?.meta?.hasDraft ?? false;
      this.state.set('isDirty', hasDraft);
    },

    AGENT_BUILDER_SET_SELECTED_NODE: (payload: { nodeId?: string }) => {
      this.state.set('selectedNodeId', payload.nodeId);
    },

    AGENT_BUILDER_SET_NAME: (payload: { name: string }) => {
      const current = this.state.get('currentAgentBuilder');
      if (current) {
        this.state.set('currentAgentBuilder', { ...current, name: payload.name });
      }
    },

    AGENT_BUILDER_MARK_CLEAN: () => {
      this.state.set('isDirty', false);
    },

    AGENT_BUILDER_MARK_DIRTY: () => {
      this.state.set('isDirty', true);
    },

    AGENT_BUILDER_SET_VERSIONS: (payload: { versions: AgentBuilderVersion[] }) => {
      this.state.set('versions', payload.versions);
      this.state.set('versionsLoading', false);
    },

    AGENT_BUILDER_SET_VERSIONS_LOADING: (payload: { loading: boolean }) => {
      this.state.set('versionsLoading', payload.loading);
    },

    AGENT_BUILDER_SET_SELECTED_VERSION: (payload: { versionId: string | null }) => {
      this.state.set('selectedVersionId', payload.versionId);
    },

    AGENT_BUILDER_SET_DRAFT_BASE_VERSION: (payload: { versionId: string | null }) => {
      this.state.set('draftBaseVersionId', payload.versionId);
    },

    AGENT_BUILDER_RESET: () => {
      this.state.set('currentAgentBuilder', undefined);
      this.state.set('selectedNodeId', undefined);
      this.state.set('isDirty', false);
      this.state.set('versions', []);
      this.state.set('selectedVersionId', null);
      this.state.set('draftBaseVersionId', null);
      this.state.set('versionsLoading', false);
    },
  };
}
